import { defineStore } from 'pinia'
// import * as Bowser from 'bowser'
import { SegmentCollection, Track } from '@ve-pro/types/track.type'
import { SegmentType } from '@ve-pro/enums/segment'
import { useTrackStore, useProjectStore } from '..'
import { isWithIn } from '@ve-pro/ve/player/utils'
import { getTracksMaxDuration } from '../tracks/tracks-actions'
import _ from 'lodash'
import { isSegmentGroup } from './segment-group'
import { getGroupDefaultSegment } from '@ve-pro/store/tracks/segment/segment-group'
import { isSegmentSubtitleGroup } from '@ve-pro/store/tracks/segment/segment-subtitle'
import { getRatioSize } from 'video-editor-renderer'

// const defaultRatio = RatioType['16:9']

// const browser = Bowser.getParser(window.navigator.userAgent)

// const isValidBrowser = browser.satisfies({
//   chrome: '>=128',
// })

export const usePlayerStore = defineStore('player', {
  state: () => {
    return {
      // 比例
      // ratio: 1,
      // 缩放
      scale: 1,
      // 引擎的缩放值
      engineScale: 1,
      zoom: 1,
      // 播放状态
      isPlaying: false,

      // 预览播放状态
      isPreviewPlaying: false,

      // 预览类型
      previewSegment: null as SegmentCollection | null,

      // 预览播放时间
      previewCurrentTime: 0,

      // unity否是加载完成
      engineReadied: false,

      // 在渲染引援中处于加载状态的数量
      loadingSegmentCount: [] as Array<SegmentCollection['id'] | Track['id']>,

      // 控制器是否拖拽中
      isDragging: false,

      // 全屏状态
      isFullscreen: false,

      // 文本编辑模式
      isTextEditing: false,

      // 取色中
      isStrawing: false,

      // 画布背景设置为透明
      backgroundAdapter: false,

      // 画布背景是否对项目内所有资产组生效
      isGroupEffect: true,

      // container loaded array
      loadedContainers: [] as Array<SegmentCollection['id'] | Track['id']>,

      // 正在删除中的元件
      deletingSegments: [] as Array<SegmentCollection['id']>,
    }
  },
  getters: {
    // 视频的总时长(单位毫秒)
    totalTime(): number {
      const trackStore = useTrackStore()
      const projectStore = useProjectStore()

      const mainTrack = trackStore.tracks.find((t) =>
        t.segments.some((s) => s.type === SegmentType.Video && s.is_main_video),
      )

      const defaultExcludeFragmentMainTrack =
        trackStore.defaultExcludeFragmentMainTrack
          ? [trackStore.defaultExcludeFragmentMainTrack]
          : []

      if (projectStore.isReplaceProject) {
        const tracks = _.cloneDeep(
          mainTrack ? [mainTrack] : defaultExcludeFragmentMainTrack,
        )

        if (mainTrack) {
          tracks.forEach((track) => {
            track.segments.forEach((segment) => {
              if (isSegmentGroup(segment)) {
                const defaultChild = getGroupDefaultSegment(segment)

                if (defaultChild) {
                  segment.start_time = defaultChild.start_time
                  segment.duration = defaultChild.duration
                }
              }
            })
          })
        }

        return getTracksMaxDuration(tracks)
      }

      return getTracksMaxDuration(
        trackStore.defaultExcludeFragmentTracks.filter(
          (t) => !t.segments.some((s) => isSegmentSubtitleGroup(s)),
        ),
      )
    },

    /** 预览总时间 */
    previewTotalTime(state) {
      if (state.previewSegment === null) return 0

      // return state.previewSegment.start_time + state.previewSegment.duration
      return state.previewSegment.duration
    },

    /** 当前模式下总时间 比如正常模式、预览模式、组合编辑模式、片段编辑模式 */
    currentTotalTime(state): number {
      const trackStore = useTrackStore()

      if (state.previewSegment) {
        return state.previewSegment.duration
      }

      if (trackStore.activeSegmentGroup && trackStore.activeChildSegment) {
        // if (
        //   trackStore.activeSegmentGroup.asset_type === AssetType.Audio ||
        //   trackStore.activeSegmentGroup.asset_type === AssetType.Video
        // ) {
        //   return trackStore.activeChildSegment.duration
        // }
        return Math.max(
          trackStore.activeSegmentGroup.duration,
          trackStore.activeChildSegment.duration,
        )
      }

      if (
        trackStore.isEnterSegmentFragmentEditMode &&
        trackStore.activeSegmentFragment
      ) {
        const tracks = trackStore.getTracksByFragmentSegmentId(
          trackStore.activeSegmentFragment.id,
        )
        return getTracksMaxDuration(tracks)
      }

      return this.totalTime
    },

    // 弹窗的预览
    isCoverPreview(state) {
      return (
        state.previewSegment !== null &&
        state.previewSegment.type !== SegmentType.Text
      )
    },

    // 当前选中可见的元件
    visibleSegments(state) {
      const trackStore = useTrackStore()

      return trackStore.activeSegments
        .map((id) => {
          return trackStore.allSegments.find((s) => s.id === id)
        })
        .filter((segment) => {
          if (!segment) return false

          if (trackStore.isEnterSegmentGroupMode && isSegmentGroup(segment)) {
            return isWithIn(
              { ...segment, start_time: 0 } as SegmentCollection,
              trackStore.currentTime,
            )
          }

          return isWithIn(segment, trackStore.currentTime)
        })
        .map((segment) => {
          if (trackStore.isEnterSegmentGroupMode && isSegmentGroup(segment)) {
            return getGroupDefaultSegment(segment)?.id ?? 0
          }

          return segment?.id ?? 0
        })
    },

    // 当前时间可见的元件
    // currentVisibleSegments() {
    //   const trackStore = useTrackStore()

    //   return trackStore.segments.filter((segment) => {
    //     return isWithIn(segment, trackStore.currentTime)
    //   })
    // },

    ratio(state) {
      // return state.engineScale * state.zoom
      // chrome 128版本的引擎返回的scale值跟低版本不一样
      // return isValidBrowser
      //   ? state.engineScale / state.scale
      //   : state.engineScale * state.zoom
      return state.engineScale / state.scale
    },

    layout() {
      const projectStore = useProjectStore()
      const [width, height] = getRatioSize(projectStore.ratio)

      return {
        width,
        height,
      }
    },
  },
  actions: {
    removeLoadingSegment(id: SegmentCollection['id'] | Track['id']) {
      const index = this.loadingSegmentCount.indexOf(id)
      if (index !== -1) {
        this.loadingSegmentCount.splice(index, 1)
      }
    },
  },
})
